import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

// Top-level viewData keys that the server can omit from the inlined bootstrap
// blob (behind the SlimViewData flag) for the client to lazy-load after first
// paint. Widen as more keys are deferred.
export type DeferredViewDataKey = "templates" | "workspaces"

interface DeferredViewDataState {
  // Keys the server omitted from viewData that are still being fetched. The
  // dashboard waits on these before rendering so store consumers (e.g.
  // useAllTemplates) don't observe a transient empty state and render stale UI.
  pendingKeys: DeferredViewDataKey[]
  // Keys whose post-paint fetch failed. The dashboard shows a blocking error
  // page (prompting a reload) rather than rendering against an empty store,
  // which would otherwise look like data loss versus the old eager render where
  // the key shipped inline in the HTML.
  failedKeys: DeferredViewDataKey[]
}

const initialState: DeferredViewDataState = { pendingKeys: [], failedKeys: [] }

const deferredViewDataSlice = createSlice({
  name: "deferredViewData",
  initialState,
  reducers: {
    beginDeferredHydration: (state, action: PayloadAction<DeferredViewDataKey[]>) => {
      state.pendingKeys = action.payload
      state.failedKeys = []
    },
    finishDeferredHydration: (state, action: PayloadAction<DeferredViewDataKey>) => {
      state.pendingKeys = state.pendingKeys.filter((key) => key !== action.payload)
    },
    failDeferredHydration: (state, action: PayloadAction<DeferredViewDataKey>) => {
      state.pendingKeys = state.pendingKeys.filter((key) => key !== action.payload)
      if (!state.failedKeys.includes(action.payload)) {
        state.failedKeys.push(action.payload)
      }
    },
  },
})

export const { beginDeferredHydration, finishDeferredHydration, failDeferredHydration } =
  deferredViewDataSlice.actions
export const DeferredViewData = deferredViewDataSlice.reducer
